import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, Form, Input, Button, Segmented, Spin, Alert } from 'antd';
import { UserOutlined, LockOutlined, PlusOutlined, KeyOutlined, LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import NavigationBar from '../../components/NavigationBar';
import DOTheme from '../../components/directOrders/doTheme';
import DynamicFormBuilderModal from '../../components/DynamicFormBuilderModal';
import ClientAdminResetModal from '../../components/clientAdmin/ClientAdminResetModal';
import useCanManageClientAdmins from '../../components/clientAdmin/useCanManageClientAdmins';
import OlderForms from '../../components/clientAdmin/OlderForms';
import { useClientAdmin } from '../../context/ClientAdminContext';
import { API_ORIGIN, PORTAL_ORIGIN } from '../../config';
import '../../styles/clientAdmin.css';

const NO_YEAR = 'other';

// "today, 09:41", "yesterday", "16 Oct", "16 Oct 2025"
const fmtLatest = (value) => {
  if (!value) return 'No responses yet';
  const d = dayjs(value);
  if (d.isSame(dayjs(), 'day')) return `Last response today, ${d.format('HH:mm')}`;
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Last response yesterday';
  return `Last response ${d.format(d.isSame(dayjs(), 'year') ? 'D MMM' : 'D MMM YYYY')}`;
};

/**
 * Client admin home: sign in as a client admin (same login as the old modal) and see every
 * dynamic form, open or closed, grouped by the year it was created.
 */
const ClientAdminHome = () => {
  const navigate = useNavigate();
  const [loginForm] = Form.useForm();
  const { login, loading: signingIn, clientAdmin } = useClientAdmin();
  const canManage = useCanManageClientAdmins();
  const [builderOpen, setBuilderOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [year, setYear] = useState(null);

  const loadForms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_ORIGIN}/api/clientadmin/forms-overview`);
      const body = await response.json().catch(() => ({}));
      if (!response.ok || body.success === false) throw new Error(body.error || body.message || 'Could not load forms');
      setData(body);
    } catch (err) {
      setError(err.message || 'Could not load forms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadForms(); }, [loadForms]);

  const handleLogin = async (values) => {
    const result = await login(values.name, values.password);
    if (result.success) {
      loginForm.resetFields();
      navigate('/client-applications', { state: { authenticated: true }, replace: true });
    }
  };

  // Forms by the year they were created, newest year first; open forms first within a year
  const groups = useMemo(() => {
    const byYear = new Map();
    (data?.forms || []).forEach((f) => {
      const key = f.year != null ? String(f.year) : NO_YEAR;
      if (!byYear.has(key)) byYear.set(key, []);
      byYear.get(key).push(f);
    });
    return [...byYear.entries()]
      .sort(([a], [b]) => (a === NO_YEAR ? 1 : b === NO_YEAR ? -1 : Number(b) - Number(a)))
      .map(([key, forms]) => ({
        key,
        label: key === NO_YEAR ? 'No date' : key,
        forms: [...forms].sort((a, b) => Number(Boolean(b.is_active)) - Number(Boolean(a.is_active))),
        open: forms.filter((f) => f.is_active).length,
        respondents: forms.reduce((sum, f) => sum + (f.respondents || 0), 0),
      }));
  }, [data]);

  const selected = groups.find((g) => g.key === year) || groups[0] || null;

  return (
    <DOTheme>
      <Layout className="do-shell do-scope ca-page">
        <NavigationBar />
        <main className="do-main ca-main">
          <aside className="ca-rail">
            <h1 className="ca-rail__name">Client admin</h1>

            {clientAdmin ? (
              <section className="ca-signin">
                <h2 className="ca-signin__title">Signed in as {clientAdmin.name}</h2>
                <p className="ca-signin__text">You're signed in as this client admin.</p>
                <Button type="primary" block onClick={() => navigate('/client-applications')}>
                  Open applications
                </Button>
              </section>
            ) : (
              <section className="ca-signin" aria-labelledby="ca-signin-title">
                <h2 className="ca-signin__title" id="ca-signin-title">Log in as a client admin</h2>
                <p className="ca-signin__text">Use the admin name and password for the client's form.</p>
                <Form form={loginForm} name="client_admin_login" layout="vertical" onFinish={handleLogin} requiredMark={false}>
                  <Form.Item name="name" label="Admin name" rules={[{ required: true, message: 'Please enter your admin name' }]}>
                    <Input prefix={<UserOutlined />} placeholder="admin_name" autoComplete="username" />
                  </Form.Item>
                  <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Please enter your password' }]}>
                    <Input.Password prefix={<LockOutlined />} placeholder="Password" autoComplete="current-password" />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" loading={signingIn} block>
                    Log in
                  </Button>
                </Form>
              </section>
            )}

            {canManage && (
              <nav className="ca-tools" aria-label="Manage client admins">
                <p className="ca-tools__label">Manage client admins</p>
                <button type="button" className="ca-tools__item" onClick={() => setBuilderOpen(true)}>
                  <PlusOutlined aria-hidden="true" />Create dynamic form
                </button>
                <button type="button" className="ca-tools__item" onClick={() => setResetOpen(true)}>
                  <KeyOutlined aria-hidden="true" />Reset a password
                </button>
              </nav>
            )}
          </aside>

          <div className="ca-sheet">
            <header className="ca-head">
              <div>
                <h2 className="ca-title">Forms</h2>
                <p className="ca-sub">Every form, open and closed, by the year it was created.</p>
              </div>
              {groups.length > 1 && (
                <Segmented
                  className="ca-years"
                  value={selected?.key}
                  onChange={setYear}
                  options={groups.map((g) => ({
                    value: g.key,
                    label: <span className="ca-years__opt">{g.label}<em className="num">{g.forms.length}</em></span>,
                  }))}
                  aria-label="Year"
                />
              )}
            </header>

            {loading && !data ? (
              <div className="ca-state"><Spin /></div>
            ) : error ? (
              <Alert
                type="error"
                showIcon
                message="Could not load forms"
                description={error}
                action={<Button size="small" icon={<ReloadOutlined />} onClick={loadForms}>Try again</Button>}
              />
            ) : !selected ? (
              <div className="ca-state">
                <strong>No forms yet</strong>
                <span>Dynamic forms appear here with their responses once they're created.</span>
              </div>
            ) : (
              <>
                <div className="ca-figs">
                  <div>
                    <b className="num">{selected.open}</b>
                    <span>open{selected.key !== NO_YEAR ? ` from ${selected.label}` : ''}</span>
                  </div>
                  <div>
                    <b className="num">{selected.forms.length - selected.open}</b>
                    <span>closed</span>
                  </div>
                  <div>
                    <b className="num">{selected.respondents.toLocaleString()}</b>
                    <span>respondents so far</span>
                  </div>
                </div>
                <ul className="ca-list">
                  {selected.forms.map((f) => (
                    <li key={f.form_id} className={`ca-row${f.is_active ? '' : ' is-closed'}`}>
                      <span className="ca-row__main">
                        <span className="ca-row__name">{f.form_name}</span>
                        {f.created_at && <span className="ca-row__meta">Created {dayjs(f.created_at).format('D MMM YYYY')}</span>}
                      </span>
                      <span className={`ca-status${f.is_active ? '' : ' is-closed'}`}>{f.is_active ? 'Open' : 'Closed'}</span>
                      <span className="ca-row__count">
                        <strong className="num">{(f.respondents || 0).toLocaleString()}</strong>
                        <small>{fmtLatest(f.latest_submission)}</small>
                      </span>
                      <span className="ca-row__action">
                        {f.form_slug && (
                          <Button icon={<LinkOutlined />} href={`${PORTAL_ORIGIN}/client-portal/${f.form_slug}`} target="_blank" rel="noopener noreferrer">
                            Open form
                          </Button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {canManage && <OlderForms />}
          </div>
        </main>
      </Layout>

      <DynamicFormBuilderModal
        visible={builderOpen}
        onClose={() => setBuilderOpen(false)}
        onSuccess={() => { setBuilderOpen(false); loadForms(); }}
      />
      <ClientAdminResetModal open={resetOpen} onClose={() => setResetOpen(false)} />
    </DOTheme>
  );
};

export default ClientAdminHome;
